// Google Places Autocomplete — uses the MODERN `PlaceAutocompleteElement`
// (Web Component) backed by the "Places API (New)". The legacy classic
// Autocomplete widget was soft-deprecated by Google in March 2025 and requires
// the legacy Places API — this implementation avoids that entirely.
//
// Reference: https://developers.google.com/maps/documentation/javascript/place-autocomplete-new

import { useEffect, useRef, useState } from "react";
import { devWarn } from "./devLog";

const GOOGLE_PLACES_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;
const SCRIPT_ID = "google-places-script";
let loadPromise = null;
let placesLibraryPromise = null;

function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.google?.maps?.importLibrary) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;
  if (!GOOGLE_PLACES_KEY) return Promise.resolve(null);

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const onReady = () => resolve(window.google);
    if (existing) {
      if (window.google?.maps?.importLibrary) onReady();
      else existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener("error", (e) => { loadPromise = null; reject(e); }, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_KEY}&v=weekly&loading=async&callback=__gpInit`;
    s.async = true;
    s.defer = true;
    window.__gpInit = onReady;
    s.onerror = (e) => { loadPromise = null; reject(e); };
    document.head.appendChild(s);
  });
  return loadPromise;
}

async function loadPlacesLibrary() {
  if (placesLibraryPromise) return placesLibraryPromise;
  placesLibraryPromise = (async () => {
    const google = await loadGoogleMaps();
    if (!google?.maps?.importLibrary) throw new Error("Google Maps loader not available");
    const places = await google.maps.importLibrary("places");
    if (!places.PlaceAutocompleteElement) {
      throw new Error("PlaceAutocompleteElement not available on this key — enable 'Places API (New)' on your Google Cloud project");
    }
    return places;
  })();
  return placesLibraryPromise;
}

// Modern PlaceAutocompleteElement returns `addressComponents` in camelCase.
// Normalize to the snake_case shape our checkout form expects.
function parsePlace(place) {
  const comp = place?.addressComponents || [];
  const find = (...types) => {
    for (const t of types) {
      const c = comp.find((x) => (x.types || []).includes(t));
      if (c) return c.longText || c.long_name || "";
    }
    return "";
  };
  const streetName = find("route");
  const streetNumber = find("street_number", "premise");
  const line1 = [streetNumber, streetName].filter(Boolean).join(" ") || place?.displayName?.text || place?.formattedAddress || "";
  const loc = place?.location;
  return {
    address_line1: line1,
    area: find("sublocality_level_1", "sublocality", "neighborhood"),
    city: find("locality", "administrative_area_level_2"),
    state: find("administrative_area_level_1"),
    zip: find("postal_code"),
    country: find("country") || "India",
    formatted_address: place?.formattedAddress || "",
    lat: typeof loc?.lat === "function" ? loc.lat() : loc?.lat ?? null,
    lng: typeof loc?.lng === "function" ? loc.lng() : loc?.lng ?? null,
  };
}

/**
 * Mount a PlaceAutocompleteElement inside `containerRef` (a div) and call
 * `onPlaceSelected(parsed)` when the user picks a suggestion.
 *
 * @param {React.RefObject<HTMLDivElement>} containerRef  Where the widget mounts
 * @param {(parsed: ReturnType<typeof parsePlace>) => void} onPlaceSelected
 */
export function useAddressAutocomplete(containerRef, onPlaceSelected) {
  const [ready, setReady] = useState(false);
  const [available] = useState(!!GOOGLE_PLACES_KEY);
  const widgetRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_PLACES_KEY) return;
    if (!containerRef.current) return;
    let cancelled = false;

    loadPlacesLibrary()
      .then((places) => {
        if (cancelled || !containerRef.current) return;
        // Prevent double-mount under React StrictMode
        containerRef.current.innerHTML = "";
        const el = new places.PlaceAutocompleteElement({
          includedRegionCodes: ["in"],           // India-only results (new API)
          types: ["geocode", "establishment"],
        });
        el.setAttribute("data-testid", "address-search");
        el.style.width = "100%";
        containerRef.current.appendChild(el);
        widgetRef.current = el;

        el.addEventListener("gmp-select", async (evt) => {
          try {
            const place = evt.placePrediction.toPlace();
            await place.fetchFields({
              fields: ["addressComponents", "formattedAddress", "location", "displayName"],
            });
            const parsed = parsePlace(place.toJSON());
            onPlaceSelected(parsed);
          } catch (err) {
            devWarn("PlaceAutocompleteElement fetchFields failed:", err);
          }
        });

        setReady(true);
      })
      .catch((err) => {
        devWarn("Google Places script failed to load:", err);
        setReady(false);
      });

    return () => {
      cancelled = true;
      if (widgetRef.current) {
        try { widgetRef.current.remove(); } catch { /* noop */ }
        widgetRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);

  return { ready, available };
}
